import React, { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./SeeParticipantsModal.module.scss";
import { CustomDialog } from "lib";
import { ParticipantsAPI, UsersAPI } from "api_inbusiness";
import { toast } from "react-toastify";
import { utils, writeFile } from "xlsx";
import dayjs from "dayjs";

// MUI imports
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
} from "@mui/material";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// User type colors and labels
const USER_TYPE_CONFIG = {
  MEMBER: { label: "Membru", color: "#4A90E2" },
  HOST: { label: "Host", color: "#9B59B6" },
  LEADER: { label: "Lider", color: "#E74C3C" },
  MANAGER: { label: "Manager", color: "#F39C12" },
  GUEST: { label: "Invitat", color: "#27AE60" },
  VOLUNTEER: { label: "Voluntar", color: "#1ABC9C" },
  EX_MEMBER: { label: "Fost Membru", color: "#95A5A6" },
};

// Filter options
const FILTER_OPTIONS = [
  { key: "all", label: "Toți" },
  { key: "attended", label: "Prezenți" },
  { key: "absent", label: "Absenți" },
];

// Individual Participant Card Component
const ParticipantCard = ({
  participant,
  onToggleAttendance,
  onDelete,
  isUpdating,
}) => {
  const user = participant.user || {};
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
  const userType = participant.userType || user.type || "MEMBER";
  const typeConfig = USER_TYPE_CONFIG[userType] || USER_TYPE_CONFIG.MEMBER;

  return (
    <div className={`${styles.participantCard} ${participant.attended ? styles.attended : styles.notAttended}`}>
      {/* Avatar */}
      <div className={styles.avatar} style={{ backgroundColor: typeConfig.color }}>
        {initials || "?"}
      </div>

      {/* Info */}
      <div className={styles.participantInfo}>
        <span className={styles.participantName}>{fullName || "Nume necunoscut"}</span>
        <Chip
          label={typeConfig.label}
          size="small"
          sx={{
            backgroundColor: `${typeConfig.color}20`,
            color: typeConfig.color,
            fontWeight: 500,
            fontSize: "0.7rem",
            height: "22px",
          }}
        />
      </div>

      {/* Actions */}
      <div className={styles.participantActions}>
        <Tooltip title={participant.attended ? "Marchează absent" : "Marchează prezent"}>
          <IconButton
            onClick={() => onToggleAttendance(participant)}
            disabled={isUpdating}
            className={styles.toggleBtn}
            size="small"
          >
            {participant.attended ? (
              <CheckCircleIcon sx={{ color: "#27AE60", fontSize: "1.5rem" }} />
            ) : (
              <CancelIcon sx={{ color: "#E74C3C", fontSize: "1.5rem" }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Șterge participant">
          <IconButton
            onClick={() => onDelete(participant.id)}
            className={styles.deleteBtn}
            size="small"
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

// Main Modal Component
const SeeParticipantsModal = ({ open, setOpen, title, id, eventDate }) => {
  // State
  const [participants, setParticipants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [showAddSection, setShowAddSection] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState("");
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch participants
  const getEventParticipants = useCallback(() => {
    if (!id) return;
    setIsLoading(true);
    ParticipantsAPI.get(id)
      .then((res) => setParticipants(res.data?.content || []))
      .finally(() => setIsLoading(false));
  }, [id]);

  // Search users for adding (debounced)
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setAddSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await UsersAPI.searchUsersAdmin(query);
      if (res.ok) {
        // Filter out users already in participants
        const participantUserIds = new Set(participants.map((p) => p.userId));
        const available = (res.data || []).filter((u) => !participantUserIds.has(u.id));
        setAddSearchResults(available.slice(0, 10)); // Limit to 10 results
      }
    } catch (error) {
      console.error("Search error:", error);
    }
    setIsSearching(false);
  }, [participants]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(addSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [addSearchQuery, searchUsers]);

  // Load data when modal opens
  useEffect(() => {
    if (open) {
      getEventParticipants();
      setSearchQuery("");
      setActiveFilter("all");
      setShowAddSection(false);
      setAddSearchQuery("");
      setAddSearchResults([]);
    }
  }, [open, id, getEventParticipants]);

  // Statistics
  const stats = useMemo(() => {
    const total = participants.length;
    const attended = participants.filter((p) => p.attended).length;
    const absent = total - attended;
    const percentage = total > 0 ? Math.round((attended / total) * 100) : 0;
    return { total, attended, absent, percentage };
  }, [participants]);

  // Filter counts
  const filterCounts = useMemo(() => ({
    all: participants.length,
    attended: participants.filter((p) => p.attended).length,
    absent: participants.filter((p) => !p.attended).length,
  }), [participants]);

  // Filtered participants
  const filteredParticipants = useMemo(() => {
    let result = [...participants];

    // Apply filter
    if (activeFilter === "attended") {
      result = result.filter((p) => p.attended);
    } else if (activeFilter === "absent") {
      result = result.filter((p) => !p.attended);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((p) => {
        const name = `${p.user?.firstName || ""} ${p.user?.lastName || ""}`.toLowerCase();
        return name.includes(query);
      });
    }

    // Sort by name
    result.sort((a, b) => {
      const nameA = `${a.user?.lastName || ""} ${a.user?.firstName || ""}`;
      const nameB = `${b.user?.lastName || ""} ${b.user?.firstName || ""}`;
      return nameA.localeCompare(nameB);
    });

    return result;
  }, [participants, activeFilter, searchQuery]);

  // Toggle attendance
  const handleToggleAttendance = async (participant) => {
    setUpdatingIds((prev) => new Set([...prev, participant.id]));
    
    try {
      const res = await ParticipantsAPI.update(participant.id, {
        attended: !participant.attended,
      });
      
      if (res.ok) {
        setParticipants((prev) =>
          prev.map((p) =>
            p.id === participant.id ? { ...p, attended: !p.attended } : p
          )
        );
        toast.success(
          !participant.attended
            ? "Prezență confirmată!"
            : "Prezență anulată!"
        );
      } else {
        toast.error("Eroare la actualizare");
      }
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
    
    setUpdatingIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(participant.id);
      return newSet;
    });
  };

  // Delete participant
  const handleDelete = async (participantId) => {
    try {
      const res = await ParticipantsAPI.delete(participantId);
      if (res.ok) {
        setParticipants((prev) => prev.filter((p) => p.id !== participantId));
        toast.success("Participant șters!");
      } else {
        toast.error("Eroare la ștergere");
      }
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Add participant (simple - no email/PDF)
  const handleAddParticipant = async (userId) => {
    try {
      const res = await ParticipantsAPI.createSimple({
        eventId: id,
        userId: userId,
        isAttending: true,
      });
      if (res.ok) {
        toast.success("Participant adăugat!");
        getEventParticipants();
        setAddSearchQuery("");
        setAddSearchResults([]);
      } else {
        toast.error("Eroare la adăugare");
      }
    } catch (error) {
      toast.error("Participantul există deja!");
    }
  };

  // Export to Excel
  const exportFile = useCallback(() => {
    const mappedParticipants = participants.map((p) => ({
      Participant: `${p.user?.lastName || ""} ${p.user?.firstName || ""}`,
      Tip: USER_TYPE_CONFIG[p.userType || p.user?.type]?.label || "Necunoscut",
      Înscris: p.isAttending ? "DA" : "NU",
      Prezent: p.attended ? "DA" : "NU",
    }));
    
    const ws = utils.json_to_sheet(mappedParticipants);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Participanți");
    writeFile(wb, `Prezenta_${dayjs(eventDate).format("DD-MM-YYYY")}.xlsx`);
  }, [participants, eventDate]);

  return (
    <CustomDialog
      open={open}
      setOpen={setOpen}
      title={title || "Participanți"}
      width="700px"
      maxWidth="900px"
      hideButtons={true}
    >
      <div className={styles.modalContainer}>
        {/* Stats Header */}
        <div className={styles.statsHeader}>
          <div className={styles.statsInfo}>
            <span className={styles.statsNumber}>{stats.attended}</span>
            <span className={styles.statsSeparator}>/</span>
            <span className={styles.statsTotal}>{stats.total}</span>
            <span className={styles.statsLabel}>prezenți ({stats.percentage}%)</span>
          </div>
          <LinearProgress
            variant="determinate"
            value={stats.percentage}
            className={styles.progressBar}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#E8F5E9",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#27AE60",
                borderRadius: 4,
              },
            }}
          />
        </div>

        {/* Search & Actions */}
        <div className={styles.searchActions}>
          <TextField
            placeholder="Caută participant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            className={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
              },
            }}
          />

          <div className={styles.actionButtons}>
            <Tooltip title="Adaugă participant">
              <IconButton
                onClick={() => setShowAddSection(!showAddSection)}
                className={`${styles.actionBtn} ${showAddSection ? styles.active : ''}`}
              >
                <PersonAddIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Descarcă Excel">
              <IconButton onClick={exportFile} className={styles.actionBtn}>
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Add User Search Section */}
        {showAddSection && (
          <div className={styles.addSection}>
            <TextField
              placeholder="Caută după nume sau email..."
              value={addSearchQuery}
              onChange={(e) => setAddSearchQuery(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAddIcon sx={{ color: "#4A90E2" }} />
                  </InputAdornment>
                ),
                endAdornment: addSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setAddSearchQuery(''); setAddSearchResults([]); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#f0f9ff",
                  borderColor: "#4A90E2",
                },
              }}
            />
            {isSearching && <div className={styles.searchingState}>Se caută...</div>}
            {addSearchResults.length > 0 && (
              <div className={styles.searchResults}>
                {addSearchResults.map((user) => (
                  <div
                    key={user.id}
                    className={styles.searchResultItem}
                    onClick={() => handleAddParticipant(user.id)}
                  >
                    <div className={styles.resultAvatar} style={{ backgroundColor: USER_TYPE_CONFIG[user.type]?.color || '#4A90E2' }}>
                      {`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()}
                    </div>
                    <div className={styles.resultInfo}>
                      <span className={styles.resultName}>{user.firstName} {user.lastName}</span>
                      <span className={styles.resultEmail}>{user.email}</span>
                    </div>
                    <Chip
                      label={USER_TYPE_CONFIG[user.type]?.label || user.type}
                      size="small"
                      sx={{
                        backgroundColor: `${USER_TYPE_CONFIG[user.type]?.color || '#4A90E2'}20`,
                        color: USER_TYPE_CONFIG[user.type]?.color || '#4A90E2',
                        fontSize: '0.7rem',
                        height: '20px',
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {addSearchQuery.length >= 2 && addSearchResults.length === 0 && !isSearching && (
              <div className={styles.noResults}>Niciun rezultat găsit</div>
            )}
          </div>
        )}

        {/* Filter Tabs */}
        <div className={styles.filterTabs}>
          {FILTER_OPTIONS.map((filter) => (
            <button
              key={filter.key}
              className={`${styles.filterTab} ${activeFilter === filter.key ? styles.active : ""}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
              <span className={styles.filterCount}>{filterCounts[filter.key]}</span>
            </button>
          ))}
        </div>

        {/* Participants List */}
        <div className={styles.participantsList}>
          {isLoading ? (
            <div className={styles.loadingState}>Se încarcă...</div>
          ) : filteredParticipants.length === 0 ? (
            <div className={styles.emptyState}>
              {searchQuery ? "Niciun rezultat pentru căutare" : "Nu există participanți"}
            </div>
          ) : (
            filteredParticipants.map((participant) => (
              <ParticipantCard
                key={participant.id}
                participant={participant}
                onToggleAttendance={handleToggleAttendance}
                onDelete={handleDelete}
                isUpdating={updatingIds.has(participant.id)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.closeBtn} onClick={() => setOpen(false)}>
            Închide
          </button>
        </div>
      </div>
    </CustomDialog>
  );
};

export default SeeParticipantsModal;
